/* global trapFocus, removeTrapFocus, fetchConfig, theme */

/* ── Room ideas grid — Room/Style filtering ────────────────────────────── */
(function () {
  if (customElements.get('room-ideas')) return;

  class RoomIdeas extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-ri-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-ri-card]'));
      this.selects = Array.from(this.querySelectorAll('[data-ri-filter]'));
      this.empty = this.querySelector('[data-ri-empty]');

      this.onChange = this.onChange.bind(this);
      this.syncRowHeight = this.syncRowHeight.bind(this);
      this.selects.forEach((select) => select.addEventListener('change', this.onChange));
      this.preselectRoom();

      if (this.grid) {
        this.syncRowHeight();
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.syncRowHeight);
          this.ro.observe(this.grid);
        }
      }
    }

    disconnectedCallback() {
      this.selects.forEach((select) => select.removeEventListener('change', this.onChange));
      if (this.ro) this.ro.disconnect();
    }

    // Row height = live column width / tile shape, so a 1 x 1 tile keeps the
    // chosen shape at any column count (the grid rows are fixed-height).
    syncRowHeight() {
      const grid = this.grid;
      if (!grid) return;

      const styles = getComputedStyle(grid);
      const cols = parseInt(styles.getPropertyValue('--ri-cols'), 10);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const width = grid.clientWidth;

      if (!width || !cols || cols < 1) return;
      if (width === this._lastWidth && cols === this._lastCols) return;
      this._lastWidth = width;
      this._lastCols = cols;

      const ratio = parseFloat(grid.dataset.riRatio) || 1;
      const colWidth = (width - gap * (cols - 1)) / cols;
      if (colWidth <= 0) return;

      grid.style.setProperty('--ri-row-h', (colWidth / ratio).toFixed(2) + 'px');
    }

    // ?room=<Room> preselects the Room filter (case-insensitive); an unknown room leaves it on All.
    preselectRoom() {
      const select = this.querySelector('[data-ri-filter="room"]');
      const room = (new URLSearchParams(window.location.search).get('room') || '').trim().toLowerCase();
      if (!select || !room) return;

      select.value = room; // no matching option leaves value ''
      const key = select.value;
      this.querySelectorAll('.rh-col-chip[data-room]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip.dataset.room === key));
      });
      this.onChange();

      // The section script refreshes the "N looks" count on a bubbled change; it may attach after us.
      const notify = () => select.dispatchEvent(new Event('change', { bubbles: true }));
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', notify, { once: true });
      else notify();
    }

    onChange() {
      const filters = {};
      this.selects.forEach((select) => {
        filters[select.dataset.riFilter] = select.value;
      });

      let visible = 0;
      this.cards.forEach((card) => {
        const roomOk = !filters.room || card.dataset.room === filters.room;
        const styleOk = !filters.style || (card.dataset.styles || '').indexOf('|' + filters.style + '|') !== -1;
        const show = roomOk && styleOk;
        card.hidden = !show;
        if (show) visible += 1;
      });

      if (this.empty) this.empty.hidden = visible !== 0;
    }
  }

  customElements.define('room-ideas', RoomIdeas);
})();

/* ── Shop the Look overlay ─────────────────────────────────────────────── */
(function () {
  if (window.__stlOverlay) return;
  window.__stlOverlay = true;

  const overlay = document.getElementById('stl-overlay');
  if (!overlay) return;

  const body = overlay.querySelector('[data-stl-body]');
  const closers = overlay.querySelectorAll('[data-stl-close]');

  let lastFocus = null;
  let currentHandle = null;
  let activeHotspot = null;

  function closeActiveHotspot() {
    if (!activeHotspot) return;
    activeHotspot.classList.remove('is-active');
    const trigger = activeHotspot.querySelector('.look__hotspot-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    activeHotspot = null;
  }

  // Slides the open popup sideways just enough to stay inside the image,
  // whatever the popup/image width. Uses layout metrics (offsetLeft/Width)
  // rather than getBoundingClientRect on the popup so the zoom-in animation
  // can't skew the measurement. --popup-shift is applied via `translate`
  // (see section-room-ideas.css), which the arrow counter-translates so it
  // keeps pointing at the pin.
  function clampPopup(hotspot) {
    const popup = hotspot.querySelector('.look__hotspot-product');
    const image = hotspot.closest('.stl-overlay__image');
    if (!popup || !image) return;

    popup.style.setProperty('--popup-shift', '0px');
    const margin = 8;
    const imageRect = image.getBoundingClientRect();
    const left = hotspot.getBoundingClientRect().left + popup.offsetLeft;
    const right = left + popup.offsetWidth;

    let shift = 0;
    if (right > imageRect.right - margin) shift = imageRect.right - margin - right;
    if (left + shift < imageRect.left + margin) shift = imageRect.left + margin - left;
    popup.style.setProperty('--popup-shift', shift + 'px');
  }

  function activateHotspot(hotspot) {
    if (hotspot === activeHotspot) {
      closeActiveHotspot();
      return;
    }
    closeActiveHotspot();
    clampPopup(hotspot);
    hotspot.classList.add('is-active');
    const trigger = hotspot.querySelector('.look__hotspot-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    activeHotspot = hotspot;
  }

  function getMiniCart() {
    return document.querySelector('mini-cart');
  }

  function handleAddAllToCart(e) {
    const btn = e.currentTarget;
    const label = btn.querySelector('[data-add-all-label]');
    let items;
    try {
      items = JSON.parse(btn.dataset.items);
    } catch (err) {
      return;
    }
    if (!Array.isArray(items) || items.length === 0) return;

    const miniCart = getMiniCart();
    const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

    if (label) label.textContent = 'Adding…';
    btn.setAttribute('disabled', 'disabled');

    const body_ = JSON.stringify({ items, sections, sections_url: window.location.pathname });

    fetch(theme.routes.cart_add_url, { ...fetchConfig('javascript'), body: body_ })
      .then((r) => r.json())
      .then((parsedState) => {
        if (parsedState.status) {
          miniCart && miniCart.failed(parsedState.description);
          throw new Error(parsedState.description || 'Add to cart failed');
        }
        if (label) label.textContent = 'Added all to cart!';
        miniCart && miniCart.added(parsedState);
      })
      .catch(() => {
        if (label) label.textContent = 'Error — try again';
      })
      .finally(() => {
        setTimeout(() => {
          if (label) label.textContent = 'Add all to cart';
          btn.removeAttribute('disabled');
        }, 2000);
      });
  }

  function showContent(handle) {
    const cache = document.getElementById('stl-content-cache');
    if (!cache) return false;
    const source = cache.querySelector(`[data-stl-content="${handle}"]`);
    if (!source) return false;

    const clone = source.cloneNode(true);
    body.replaceChildren(clone);
    // Cloned nodes lose their custom-element upgrade (e.g. <add-to-cart>,
    // <slider-component>) — re-run it now the clone has real content and is
    // attached to the document.
    customElements.upgrade(body);

    body.querySelectorAll('[data-hotspot]').forEach((hotspot) => {
      const trigger = hotspot.querySelector('.look__hotspot-trigger');
      if (trigger) {
        trigger.addEventListener('click', () => activateHotspot(hotspot));
      }
    });

    const addAllBtn = body.querySelector('[data-hotspot-add-all]');
    if (addAllBtn) addAllBtn.addEventListener('click', handleAddAllToCart);

    const firstFocusable = body.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();
    return true;
  }

  const LOOK_PARAM = 'look';
  let openedViaPush = false;

  function getLookParam() {
    return new URLSearchParams(window.location.search).get(LOOK_PARAM);
  }

  function buildUrlWithLook(handle) {
    const url = new URL(window.location.href);
    if (handle) {
      url.searchParams.set(LOOK_PARAM, handle);
    } else {
      url.searchParams.delete(LOOK_PARAM);
    }
    return url;
  }

  function doOpen(handle, trigger) {
    if (!handle) return false;
    lastFocus = trigger || document.activeElement;
    currentHandle = handle;

    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    if (!showContent(handle)) {
      doClose();
      return false;
    }

    trapFocus(overlay.querySelector('.stl-overlay__panel'));

    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
    });
    return true;
  }

  function doClose() {
    if (overlay.hidden) return;
    overlay.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    currentHandle = null;
    activeHotspot = null;
    removeTrapFocus();

    const onEnd = (e) => {
      if (e.target !== overlay.querySelector('.stl-overlay__panel')) return;
      overlay.hidden = true;
      body.innerHTML = '';
      overlay.removeEventListener('transitionend', onEnd);

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      } else {
        const grid = document.querySelector('room-ideas');
        if (grid && typeof grid.focus === 'function') grid.focus();
      }
    };
    overlay.addEventListener('transitionend', onEnd);
  }

  function open(handle, trigger) {
    if (!handle) return;
    const opened = doOpen(handle, trigger);
    if (!opened) return;

    openedViaPush = true;
    history.pushState({ stlLook: handle }, '', buildUrlWithLook(handle));
  }

  function close() {
    if (overlay.hidden) return;

    if (openedViaPush) {
      history.back();
    } else {
      history.replaceState(null, '', buildUrlWithLook(null));
      doClose();
    }
  }

  function syncToUrl() {
    const handle = getLookParam();
    if (handle) {
      if (handle !== currentHandle) doOpen(handle, null);
    } else if (currentHandle) {
      doClose();
    }
  }

  window.addEventListener('popstate', () => {
    openedViaPush = false;
    syncToUrl();
  });

  const initialHandle = getLookParam();
  if (initialHandle) {
    openedViaPush = false;
    doOpen(initialHandle, null);
  }

  document.addEventListener('click', (e) => {
    const tile = e.target.closest('[data-look-handle]');
    if (!tile) return;
    e.preventDefault();
    open(tile.getAttribute('data-look-handle'), tile);
  });

  closers.forEach((el) => el.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (activeHotspot) {
      closeActiveHotspot();
      return;
    }
    if (overlay.classList.contains('is-open')) close();
  });

  document.addEventListener('click', (e) => {
    if (!activeHotspot) return;
    if (activeHotspot.contains(e.target)) return;
    closeActiveHotspot();
  });
})();
